import { FC, useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, getVesselsByCompany } from '../core/_requests'
import { CompanyGroupAdmin, CreateInventoryItemHeadRequest, InventoryItemHead, Vessel } from '../core/_models'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope, libraryVesselScopeLocked } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditHeadModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (id: number, payload: CreateInventoryItemHeadRequest) => Promise<void>
  head: InventoryItemHead | null
  cgaId?: number
  vesselId?: number
}

const EditHeadModal: FC<EditHeadModalProps> = ({ visible, onClose, onSubmit, head, cgaId, vesselId }) => {
  const { currentUser } = useAuth()
  const [name, setName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
  )
  const vesselScopeLocked = libraryVesselScopeLocked(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'
  const effectiveCgaId = libraryScopeCompany ? procurementTenantCgaId(currentUser) : undefined

  const loadCompanies = useCallback(async () => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getCompanyGroupAdmins()
      setCompanies(data)
    } catch (error) {
      console.error('Error loading companies:', error)
      toast.error('Failed to load companies')
      setCompanies([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVessels = useCallback(async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getVesselsByCompany(companyUsername)
      setVessels(data || [])
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels')
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVesselsForCurrentUser = useCallback(async () => {
    const companyUsername =
      (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
    if (!companyUsername) return
    await loadVessels(companyUsername)
  }, [currentUser, loadVessels])

  useEffect(() => {
    if (visible && head) {
      setName(head.name)

      if (libraryScopeAll) {
        setSelectedCompanyId(head?.cgaid?.id ?? cgaId ?? '')
        setSelectedVesselId(head?.vesselId ?? vesselId ?? '')
      } else if (libraryScopeCompany) {
        setSelectedCompanyId('')
        setSelectedVesselId(head?.vesselId ?? vesselId ?? '')
      } else {
        setSelectedCompanyId('')
        setSelectedVesselId((currentUser as any)?.vessel?.id ?? head?.vesselId ?? vesselId ?? '')
      }
    }
  }, [visible, head, libraryScopeAll, libraryScopeCompany, cgaId, vesselId, currentUser])

  useEffect(() => {
    if (!visible || !head) return

    if (libraryScopeAll) {
      loadCompanies()
    } else if (libraryScopeCompany) {
      loadVesselsForCurrentUser()
    }
  }, [visible, head, libraryScopeAll, libraryScopeCompany, loadCompanies, loadVesselsForCurrentUser])

  useEffect(() => {
    if (!visible || !libraryScopeAll || !head) return
    if (!selectedCompanyId) {
      setVessels([])
      setSelectedVesselId('')
      return
    }

    const selectedCompany = companies.find((c) => c.id === Number(selectedCompanyId))
    if (selectedCompany?.username) {
      loadVessels(selectedCompany.username)
    }
  }, [visible, libraryScopeAll, head, selectedCompanyId, companies, loadVessels])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!head) return

    if (!name.trim()) {
      toast.error('Please enter a head name')
      return
    }

    let scopedCgaId: number | null = null
    let scopedVesselId: number | null = null

    if (libraryScopeAll) {
      if (!selectedCompanyId) {
        toast.error('Please select a company')
        return
      }
      scopedCgaId = Number(selectedCompanyId)
      scopedVesselId = selectedVesselId ? Number(selectedVesselId) : null
    } else if (libraryScopeCompany) {
      const fallbackCgaId = effectiveCgaId ?? cgaId ?? null
      if (!fallbackCgaId) {
        toast.error('Unable to resolve company scope for this user')
        return
      }
      scopedCgaId = Number(fallbackCgaId)
      scopedVesselId = selectedVesselId ? Number(selectedVesselId) : null
    } else {
      const userVesselId = (currentUser as any)?.vessel?.id ?? vesselId ?? head?.vesselId ?? null
      scopedVesselId = userVesselId ? Number(userVesselId) : null
      scopedCgaId = head?.cgaid?.id ?? cgaId ?? null
    }

    const payload: CreateInventoryItemHeadRequest = {
      name: name.trim(),
      active: true,
      inventoryItemCategoryId: null,
      cgaid: scopedCgaId ? { id: scopedCgaId } : null,
      vesselId: scopedVesselId ?? 0,
    }

    if (!payload.cgaid && !payload.vesselId) {
      toast.error('Please select a valid company or vessel scope')
      return
    }
    if (vesselScopeLocked && !scopedVesselId) {
      toast.error('Vessel is required')
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(head.id, payload)
      toast.success('Head updated successfully!')
      handleClose()
    } catch (error: any) {
      console.error('Error updating head:', error)
      toast.error('Failed to update head')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setName('')
    setCompanies([])
    setVessels([])
    setSelectedCompanyId('')
    setSelectedVesselId('')
    onClose()
  }

  if (!visible || !head) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header border-bottom'>
            <h3 className='modal-title fw-bold text-dark'>Edit Head</h3>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {libraryScopeAll && (
                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold text-muted fs-7 required'>Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyId}
                      onChange={(e) => {
                        const nextCompanyId = e.target.value ? Number(e.target.value) : ''
                        setSelectedCompanyId(nextCompanyId)
                        setSelectedVesselId('')
                      }}
                      disabled={isLoadingDropdowns}
                      required
                    >
                      <option value=''>Select a company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedVesselId}
                      onChange={(e) => {
                        const nextVesselId = e.target.value ? Number(e.target.value) : ''
                        setSelectedVesselId(nextVesselId)
                      }}
                      disabled={isLoadingDropdowns || !selectedCompanyId}
                    >
                      <option value=''>Company level (no vessel)</option>
                      {vessels.map((vessel) => (
                        <option key={vessel.id} value={vessel.id}>
                          {vessel.fleet_name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                </div>
              )}

              {libraryScopeCompany && (
                <div className='mb-4'>
                  <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId}
                    onChange={(e) => {
                      const nextVesselId = e.target.value ? Number(e.target.value) : ''
                      setSelectedVesselId(nextVesselId)
                    }}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>Company level (no vessel)</option>
                    {vessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='mb-4'>
                <label className='form-label fw-semibold text-muted fs-7 required'>Head Name</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Enter head name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className='modal-footer border-top'>
              <button
                type='button'
                className='btn btn-light'
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn_primary'
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Head'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditHeadModal
